import {chain, isEmpty, isNull, noop} from 'lodash';

import {PromiseCompletionSource} from '../../helpers/asyncHelpers';
import {batch} from '../../helpers/reduxHelpers';
import {DataGroupModel, mergeDataGroups} from '../../models/api/dataGroupModel';
import {AppState} from '../../store/appState';
import {Dispatch, StateGetter} from '../../store/appStore';
import {ThunkAction} from '../../store/appThunk';
import {updateDataAction} from '../../store/data/dataGroupsActions';
import {getOptimisticData, getOriginalData} from '../../store/data/dataSelectors';
import {DataState} from '../../store/data/dataState';
import {findGlobalLock} from '../locks/globalLocks';
import {Lock} from '../locks/lock';
import {taskQueues} from './globalTaskQueues';

/**
 * @file
 * This is the legacy task queue system, which we plan to deprecate because it has known issues. It has been
 * superseded by the new optimistic update system (see optimisticUpdatesActions), resource locks (simple
 * mutexes), and resource tables (which allow tombstoning).
 */

/*
 * Types.
 */

type DeleteActionCreator = () => ThunkAction;
export type ModelGetter<T> = (dataGroup: DataGroupModel, dataState: DataState) => T | undefined;
export type DataGroupGetter<T> = (model: T) => DataGroupModel;
type ResultPartitioner = (dataGroup: DataGroupModel) => readonly [DataGroupModel, DataGroupModel];

export interface TaskQueue<T> {
  performTaskAsync(task: TaskQueueItem<T>): Promise<T | null>;

  get(): T | undefined;
  getResult(): T | undefined;

  update(dataGroup: DataGroupModel): void;
  delete(): void;

  waitForTurnAsync(): Promise<void>;
  provideResultAsync(dataGroup: DataGroupModel | null): Promise<T | null>;
  reportError(): void;
  reportDeleted(): void;
}

export interface TaskQueueItem<T> {
  getOptimisticResult?: (resource: T | undefined) => DataGroupModel | null;
  getResultAsync: (resource: T | undefined) => Promise<DataGroupModel | null>;
}

/*
 * API.
 */

export function findQueue<T>(
  dispatch: Dispatch,
  getState: StateGetter,
  queueName: string,
  modelGetter: ModelGetter<T>,
  dataGroupGetter: DataGroupGetter<T>,
  resultPartitioner?: ResultPartitioner,
  deleteActionCreator?: DeleteActionCreator,
): TaskQueue<T> {
  // Check if we already have a queue with this name.
  const queue = taskQueues[queueName];
  if (queue) {
    return queue as TaskQueue<T>;
  }

  // Otherwise, create it.
  const newQueue = new PromiseTaskQueue<T>(
    dispatch,
    getState,
    queueName,
    modelGetter,
    dataGroupGetter,
    resultPartitioner,
    deleteActionCreator,
  );
  taskQueues[queueName] = newQueue;

  return newQueue;
}

/*
 * Single queue class & logic.
 */

// This retains legacy behavior, which is to feed the whole data group into the queue.
const defaultResultPartitioner: ResultPartitioner = (dataGroup) => [dataGroup, {}];

class PromiseTaskQueue<T> implements TaskQueue<T> {
  constructor(
    dispatch: Dispatch,
    getState: StateGetter,
    queueName: string,
    modelGetter: ModelGetter<T>,
    dataGroupGetter: DataGroupGetter<T>,
    resultPartitioner: ResultPartitioner = defaultResultPartitioner,
    deleteActionCreator?: DeleteActionCreator,
  ) {
    this.queueName = queueName;
    this.lock = findGlobalLock(queueName);
    this.dispatch = dispatch;
    this.getState = getState;
    this.modelGetter = modelGetter;
    this.dataGroupGetter = dataGroupGetter;
    this.resultPartitioner = resultPartitioner;
    this.deleteActionCreator = deleteActionCreator;

    this.originalState = getState();
    this.results = [];
    this.isOptimisticallyDeleted = false;
    this.isDeleted = false;
    this.finalizeSource = new PromiseCompletionSource<T | null>();
  }

  private readonly queueName: string;
  private readonly lock: Lock;
  private readonly dispatch: Dispatch;
  private readonly getState: StateGetter;
  private readonly modelGetter: ModelGetter<T>;
  private readonly dataGroupGetter: DataGroupGetter<T>;
  private readonly resultPartitioner: ResultPartitioner;
  private readonly deleteActionCreator?: DeleteActionCreator;

  private readonly originalState: AppState;
  private readonly results: Array<DataGroupModel | null>;
  private isOptimisticallyDeleted: boolean;
  private isDeleted: boolean;
  private finalizeSource: PromiseCompletionSource<T | null>;

  async performTaskAsync(task: TaskQueueItem<T>) {
    if (task.getOptimisticResult) {
      const optimisticResult = task.getOptimisticResult(this.get());
      if (isNull(optimisticResult)) {
        this.delete();
      } else {
        this.update(optimisticResult);
      }
    }

    await this.waitForTurnAsync();

    const getResultAsync = async () => {
      try {
        return await task.getResultAsync(this.getResult());
      } catch (error) {
        this.reportError();
        throw error;
      }
    };
    const result = await getResultAsync();

    if (isNull(result)) {
      this.reportDeleted();
      return null;
    }

    // Use the queue only for the resource. We'll return the rest to be dispatched as a DATA_UPDATED.
    const [resourceResult, otherResult] = this.resultPartitioner(result);
    return batch(() => {
      if (!isEmpty(otherResult)) {
        this.dispatch(updateDataAction(otherResult));
      }

      return this.provideResultAsync(resourceResult);
    });
  }

  get() {
    return this.modelGetter({}, getOptimisticData(this.getState()));
  }

  getResult() {
    if (this.isDeleted) {
      return undefined;
    }

    return this.modelGetter(this.buildFinalDataGroup(), getOriginalData(this.originalState));
  }

  /** Update the resource instantly, before any async work is done. */
  update(dataGroup: DataGroupModel) {
    if (this.isOptimisticallyDeleted) {
      return;
    }

    if (isEmpty(dataGroup)) {
      return;
    }

    this.dispatch(updateDataAction(dataGroup));
  }

  /** Delete the resource instantly, before any async work is done. */
  delete() {
    if (this.isOptimisticallyDeleted) {
      return;
    }

    // Check if this queue supports deletion.
    if (!this.deleteActionCreator) {
      throw new Error('This queue does not support deletion.');
    }

    this.dispatch(this.deleteActionCreator());
    this.isOptimisticallyDeleted = true;
  }

  /** Wait in line for our turn to act on the protected resource. */
  async waitForTurnAsync() {
    await this.lock.lockAsync();
  }

  /** Provide the result of the async operation. Wait for the merge to complete. */
  provideResultAsync(dataGroup: DataGroupModel | null): Promise<T | null> {
    // Add the updated model to our results.
    this.results.push(dataGroup);

    // Wait on the queue to finalize.
    return this.flushAsync();
  }

  /** Processes the rest of the queue, and resolves when the queue is empty. */
  private async flushAsync() {
    this.lock.unlock();

    // If this was the last waiter, finalize.
    if (!this.lock.isLocked) {
      this.finalizeAsync();
    }

    // Wait on the queue to finalize.
    return this.finalizeSource.promise;
  }

  /** Report on an error that happened while the task was working. */
  reportError() {
    // Allow the queue to continue.
    this.flushAsync();
  }

  /** Report that the resource was deleted. */
  reportDeleted() {
    if (this.isDeleted) {
      return;
    }

    // Check if this queue supports deletion.
    if (!this.deleteActionCreator) {
      throw new Error('This queue does not support deletion.');
    }

    this.dispatch(this.deleteActionCreator());
    this.isDeleted = true;

    // Allow the queue to continue.
    this.flushAsync();
  }

  private buildFinalDataGroup() {
    // Sort the results.
    const sortedDataGroups = chain(this.results)
      .compact()
      .sortBy((r) => r.updatedAt)
      .value();

    // Then merge them into the final group.
    return mergeDataGroups(...sortedDataGroups);
  }

  private async finalizeAsync() {
    // If the resource was deleted, replace the queue with a no-op tombstone so it never gets re-created.
    if (this.isDeleted) {
      taskQueues[this.queueName] = noopTaskQueue;
      this.finalizeSource.resolve(null);
      return;
    }

    // Remove the queue from the map.
    delete taskQueues[this.queueName];

    // Build the final data group from our different results.
    const finalDataGroup = this.buildFinalDataGroup();

    // If there are no data groups, nothing to do.
    if (isEmpty(finalDataGroup)) {
      const model = this.modelGetter({}, getOriginalData(this.originalState)) || null;
      await this.restoreOriginalStateAsync(model);
      this.finalizeSource.resolve(model);
      return;
    }

    // Otherwise, update the state.
    this.dispatch(updateDataAction(finalDataGroup));

    // Then, return the final model.
    const model = this.modelGetter({}, getOriginalData(this.getState())) || null;
    this.finalizeSource.resolve(model);
  }

  private async restoreOriginalStateAsync(model: T | null) {
    // If there was no model, remove it from the store.
    if (!model && this.deleteActionCreator) {
      this.deleteActionCreator();
      return;
    }

    if (!model) {
      return;
    }

    // Otherwise, update it.
    const dataGroup = this.dataGroupGetter(model);
    this.dispatch(updateDataAction(dataGroup, {shouldIgnoreUpdatedAt: true}));
  }
}

/*
 * No-op task queue.
 */

export const noopTaskQueue: TaskQueue<any> = Object.freeze({
  performTaskAsync: async () => null,
  get: () => undefined,
  getResult: () => undefined,
  update: noop,
  delete: noop,
  waitForTurnAsync: async () => undefined,
  provideResultAsync: async () => null,
  reportError: noop,
  reportDeleted: noop,
});
